import { MONTHLY_PLATFORMS, MONTHLY_SOURCES, sourcesForPlatform } from './sourceConfig';

export const COMMON_WAREHOUSE_RENT_PAGE = { platform: '公共', key: 'rent', label: '仓租费用', kind: 'rent', common: true, description: '四个平台共用的仓租费用来源；按归属平台与模式校验，确认入账后分别归集至对应月报。' };
export const COMMON_WAREHOUSE_RENT_PATH = '/niuhui/threeDragonWarehouseRent';

/** 固定 URL 与独立路由名称供菜单、系统工作页及平台入口共同使用。 */
export const DATA_SOURCE_PAGES = [
  { platform: 'Shein', key: 'primage', label: '运费补贴', modes: ['shein-half', 'shein-third'], period: 'dateRange', refresh: 'fetchData', component: () => import('../sheinSemi/primage.vue'), description: '按订单创建时间查看运费补贴，保留原有订单查询和 Excel 导入。' },
  { platform: 'Shein', key: 'sfsOrder', label: 'SFS 订单', modes: ['shein-half', 'shein-third'], period: 'shared', component: () => import('../sheinSemi/sfsOrder.vue'), description: '按订单号维护 SFS 订单及运单，保留原有查询和 Excel 导入。' },
  { platform: 'Shein', key: 'onlinePlaceOrder', label: '在线下单', modes: ['shein-half'], period: 'dateRange', refresh: 'fetchData', component: () => import('../sheinSemi/onlinePlaceOrder.vue'), description: '按揽收时间查看在线下单数据，保留原有查询和 Excel 导入。' },
  { platform: 'Shein', key: 'refund', label: '新增退款', kind: 'REFUND', modes: ['shein-half', 'shein-third'], description: '按月份、模式和店铺维护新增退款，确认后计入经营月报。' },
  { platform: 'Shein', key: 'bill', label: '结算账单', kind: 'BILL', modes: ['shein-half', 'shein-third'], description: '保留原始结算字段，识别账单类型、补充分类并确认入账。' },
  { platform: 'Temu', key: 'waybillFee', label: '面单费用', modes: ['temu-half'], period: 'month', state: 'searchForm', field: 'accountingMonth', refresh: 'getPageList', component: () => import('../temuSemiV2/temuWaybill.vue'), description: '按账务月份维护面单费用，保留原有新增、导入和导出流程。' },
  { platform: 'Temu', key: 'temuFullOverdueSkuMapping', label: '超期 SKU 映射', modes: ['temu-full'], period: 'shared', component: () => import('../temuFull/temuFullOverdueSkuMapping.vue'), description: '维护超期库存的子 SKU 与主 SKU 对应关系。' },
  { platform: 'Temu', key: 'temuFullOverdueSku', label: '超期 SKU 数据', modes: ['temu-full'], period: 'referenceMonth', component: () => import('../temuFull/temuFullOverdueSku.vue'), description: '按期间查看库存超 271 天 SKU 清单，保留原有查询和 Excel 导入。' },
  { platform: 'Walmart', key: 'walmartDropShipping', label: '一件代发', modes: ['walmart'], period: 'shared', component: () => import('../walmart/walmartDropShipping.vue'), description: '按店铺、订单和物流单号维护一件代发数据，保留原有导入与导出。' },
  { platform: 'Walmart', key: 'walmartAdvertisingBill', label: '广告费用', modes: ['walmart'], period: 'month', state: 'query', field: 'advertisingMonth', refresh: 'fetchBills', component: () => import('../walmart/walmartAdvertisingBill.vue'), description: '按广告月份查看广告账单，保留原有店铺选择和账单导入。' },
  COMMON_WAREHOUSE_RENT_PAGE,
];

export const dataSourcesForPlatform = platform => DATA_SOURCE_PAGES.filter(page => page.platform === platform);
export const findDataSourcePage = (platform, sourceKey) => DATA_SOURCE_PAGES.find(page => page.platform === platform && page.key === sourceKey);
export const modesForDataSource = page => page ? page.common ? MONTHLY_SOURCES : sourcesForPlatform(page.platform).filter(source => !page.modes || page.modes.includes(source.key)) : [];
export const dataSourcePath = page => page.common ? COMMON_WAREHOUSE_RENT_PATH : `/niuhui/threeDragon${page.platform}Source/${page.key}`;
export const dataSourceRouteName = page => page.common ? 'threeDragonWarehouseRent' : `threeDragon${page.platform}Source-${page.key}`;

/** 将返回的记录展开到既有 /niuhui 的 children；不改旧路由或旧数据源接口。 */
export function createDataSourceRoutes() {
  const routes = DATA_SOURCE_PAGES.map(page => ({
    path: page.common ? 'threeDragonWarehouseRent' : `threeDragon${page.platform}Source/${page.key}`,
    name: dataSourceRouteName(page),
    components: { table: () => import('./dataSourcePage.vue') },
    props: { table: { platform: page.platform, sourceKey: page.key } },
    meta: {
      workspaceTitle: page.common ? page.label : `${page.platform} · ${page.label}`,
      documentTitle: page.common ? page.label : `${page.platform} · ${page.label}`,
      breadcrumb: page.common ? ['三小龙月报', page.label] : ['三小龙月报', `${page.platform} 月报`, page.label],
      platform: page.platform,
      sourceKey: page.key,
    },
  }));
  // 旧分享链接继续有效；未指定有效模式时进入公共的全部平台范围。
  return routes.concat(MONTHLY_PLATFORMS.map(platform => ({
    path: `threeDragon${platform.key}Source/rent`,
    name: `threeDragon${platform.key}Source-rent`,
    redirect: route => ({ path: COMMON_WAREHOUSE_RENT_PATH, query: { ...route.query } }),
  })));
}

export function platformReportRoute(platform) {
  const config = MONTHLY_PLATFORMS.find(item => item.key === platform);
  return config && config.route;
}

export function monthDateRange(month) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month || '')) return [];
  const [year, number] = month.split('-').map(Number);
  const finalDay = new Date(year, number, 0).getDate();
  return [`${month}-01 00:00:00`, `${month}-${String(finalDay).padStart(2, '0')} 23:59:59`];
}

/** 仅在独立页中适配原查询状态。导入、导出、权限与 sourceType 均沿用原组件。 */
export function legacyDataSourceComponent(page) {
  if (!page || !page.component) return null;
  if (page.period === 'shared') return page.component;
  return () => page.component().then(module => {
    const original = module.default || module;
    if (page.period === 'referenceMonth') {
      const reference = original.components.TemuFullReferencePage;
      return {
        name: 'ThreeDragonPeriodOverdueSkuSource',
        extends: original,
        components: {
          TemuFullReferencePage: {
            extends: reference,
            methods: {
              getPageList(...args) {
                // 原参考页可清空期间；独立账务页查询仍必须维持当前月份。
                if (!this.searchForm.period) {
                  const field = this.searchFields.find(item => item.prop === 'period');
                  this.searchForm.period = field && field.defaultValue || '';
                }
                return reference.methods.getPageList.apply(this, args);
              },
            },
          },
        },
        props: { reportMonth: { type: String, default: '' } },
        data() {
          return { searchFields: original.data.call(this).searchFields.map(field => field.prop === 'period' ? { ...field, defaultValue: this.reportMonth } : field) };
        },
        watch: {
          reportMonth(month) {
            this.searchFields = this.searchFields.map(field => field.prop === 'period' ? { ...field, defaultValue: month } : field);
            const child = this.$children.find(item => item.$options.name === 'TemuFullReferencePage');
            if (child && child.searchForm.period !== month) { child.searchForm.period = month; child.handleSearch(); }
          },
        },
        mounted() {
          const child = this.$children.find(item => item.$options.name === 'TemuFullReferencePage');
          if (child) this.stopPeriodWatch = child.$watch('searchForm.period', month => { if (month) this.$emit('report-month-change', month); });
        },
        beforeDestroy() { if (this.stopPeriodWatch) this.stopPeriodWatch(); },
      };
    }
    const state = page.period === 'dateRange' ? 'searchForm' : page.state;
    const field = page.period === 'dateRange' ? 'dateRange' : page.field;
    const valueForMonth = month => page.period === 'dateRange' ? monthDateRange(month) : month;
    const options = {
      name: `ThreeDragonSource${page.platform}${page.key}`,
      extends: original,
      props: { reportMonth: { type: String, default: '' } },
      data() { return { [state]: { [field]: valueForMonth(this.reportMonth) } }; },
      watch: {
        reportMonth(month) {
          const value = valueForMonth(month);
          if (JSON.stringify(this[state][field]) === JSON.stringify(value)) return;
          this[state][field] = value;
          this.pageNum = 1;
          this[page.refresh]();
        },
      },
      methods: {
        [page.refresh](...args) {
          const value = this[state][field];
          if (!value || (Array.isArray(value) && !value.length)) this[state][field] = valueForMonth(this.reportMonth);
          return original.methods[page.refresh].apply(this, args);
        },
      },
    };
    // 原月选择器仍可使用；原页的查询按钮 / @change 流程保持原样。
    if (page.period === 'month') options.watch[`${state}.${field}`] = function(month) { if (month) this.$emit('report-month-change', month); };
    return options;
  });
}
